import React, { useEffect, useMemo, useState, useRef, lazy, Component } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ShoppingCart, Phone, Mail, Check, ChevronLeft, ChevronRight, MessageSquare, Info, ChevronDown, FileText, Download, Truck, Calculator as CalcIcon, X, Plus, Minus } from 'lucide-react';
import { categories } from '../data/products';
import { useCart } from '../components/CartContext';
import { QuoteModal } from '../components/QuoteModal';
import { SameDayBadge } from '../components/SameDayBadge';
import { generateSpecSheet } from '../utils/generateSpecSheet';
import { buildDeliveryLineItems, getDefaultQuantity, getSmallLoadSurcharge } from '../utils/loadSplit';
import { CartHelper } from '../components/CartHelper';
import { SimpleAreaCalculator } from '../components/SimpleAreaCalculator';
import { ProductComparisonPanel } from '../components/ProductComparisonPanel';
function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
  className = ''
}: {
  value: string;
  onChange: (val: string) => void;
  options: {
    value: string;
    label: string;
    disabled?: boolean;
  }[];
  placeholder: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedOption = options.find(opt => opt.value === value);
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-2853" data-mp="1ovhm22r3owyg">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full px-4 py-3 bg-[#e8ede4] dark:bg-emerald-500/[0.07] border border-[#d4ddcf] dark:border-emerald-500/20 rounded-none text-brand-charcoal dark:text-white focus:outline-none focus:ring-2 focus:ring-[#5fb478] focus:border-transparent transition-all flex items-center justify-between font-medium hover:bg-[#dde5d6] dark:hover:bg-emerald-500/[0.12] ${value ? '' : 'text-stone-500'}`} data-id="element-2854" data-mp="1jxg27fluotlx">
        <span className="truncate" data-id="element-2855" data-mp="39euvc123glf0">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2856" data-mp="wowv9208zkw" />
      </button>

      <AnimatePresence data-id="element-2857" data-mp="3o3rasj6hd0pn">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 w-full top-full mt-2 bg-white dark:bg-stone-900 border border-[#d4ddcf] dark:border-white/10 rounded-none shadow-xl overflow-hidden max-h-60 overflow-y-auto" data-id="element-2858" data-mp="3o2pnngc6wnrk">
            {options.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
          if (!opt.disabled) {
            onChange(opt.value);
            setIsOpen(false);
          }
        }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${opt.disabled ? 'text-stone-300 dark:text-white/30 cursor-not-allowed bg-stone-50 dark:bg-stone-900/50' : value === opt.value ? 'bg-[#e3f7e8] dark:bg-[#0f3e1e] text-[#1a7a3a] dark:text-[#a8e8b8] font-semibold' : 'text-brand-charcoal dark:text-white hover:bg-stone-50 dark:hover:bg-white/10'}`} data-id="element-2859" data-mp="298hn6kl394mm">
                {opt.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const ProductImageCarousel = ({
  images,
  alt
}: {
  images: string[];
  alt: string;
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
  }, [images]);
  if (!images || images.length <= 1) {
    return <img src={images[0]} alt={alt} className="absolute inset-0 w-full h-full object-cover" data-id="element-2860" data-mp="12oi1q23ezwkr" />;
  }
  return <div className="absolute inset-0 w-full h-full group" data-id="element-2861" data-mp="a0j12u8h9o1f">
      <img src={images[idx]} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" data-id="element-2862" data-mp="a12jsd5ycggi" />
      <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2863" data-mp="exsyox60t60c">
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === 0 ? images.length - 1 : i - 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2864" data-mp="opyrg943jrgl">
          <ChevronLeft className="w-5 h-5" data-id="element-2865" data-mp="xr6oyoysgo9m" />
        </button>
        <button onClick={e => {
        e.stopPropagation();
        setIdx(i => i === images.length - 1 ? 0 : i + 1);
      }} className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors" data-id="element-2866" data-mp="12p83cwl9rzer">
          <ChevronRight className="w-5 h-5" data-id="element-2867" data-mp="34g1r475evmxu" />
        </button>
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-2868" data-mp="34ieex0xuorit">
        {images.map((_, i) => <button key={i} onClick={e => {
        e.stopPropagation();
        setIdx(i);
      }} className={`w-2 h-2 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/50 hover:bg-white/75'}`} data-id="element-2869" data-mp="2s6c3wsp6a0lw" data-mpi={i} />)}
      </div>
    </div>;
};
export function Product() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    addToCart,
    openCart
  } = useCart();
  // Lazy-init the default quantity per product. Soil/Mulch/Org. products
  // (topsoil, bark, compost, play chips, fill dirt) default to a 15-yd
  // light load — that's the typical homeowner spring-project size and
  // the size of one full light-material truckload. The 50/50 Mix is
  // the explicit exception (defaults to 12 — see getDefaultQuantity in
  // utils/loadSplit.ts). Everything else (rock, drain, quarry, decor)
  // defaults to a 12-yd heavy load.
  const [quantity, setQuantity] = useState<string>(() => {
    for (const cat of categories) {
      const found = cat.products.find(p => p.slug === slug);
      if (found) return getDefaultQuantity(found.name);
    }
    return '12';
  });
  const [numLoads, setNumLoads] = useState<string>('');
  const [addedFlash, setAddedFlash] = useState(false);
  // Click-to-expand explainer for the Small-load rate label that now
  // sits ABOVE the big price number when the order is under the
  // small-load threshold. Replaces the verbose "surcharge applies"
  // subtext with a one-tap pulldown of plain-language pricing context.
  const [showSmallLoadInfo, setShowSmallLoadInfo] = useState(false);
  // Auto-dismiss the "Added to Truck!" confirmation card after 10s and
  // revert to the default Add-to-Truck state. Customer can still tap
  // Continue Shopping / View Cart at any point during the window.
  useEffect(() => {
    if (!addedFlash) return;
    const t = setTimeout(() => setAddedFlash(false), 10000);
    return () => clearTimeout(t);
  }, [addedFlash]);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  // True when + has been pressed past 4 loads. Replaces the auto-fire
  // QuoteModal — the customer sees an amber "5+ loads" link below the
  // stepper and explicitly taps it to open the form.
  const [showQuoteWarning, setShowQuoteWarning] = useState(false);
  // Free-text yardage input — always active (no tap-to-edit handshake).
  // The readout *is* a transparent input styled to match the bold display.
  // Max 2 digits, clamped 1–96 on blur/Enter. The +/− pads keep this in
  // sync by writing to qtyDraft alongside quantity via the useEffect below.
  const [qtyDraft, setQtyDraft] = useState('12');
  const qtyInputRef = useRef<HTMLInputElement>(null);
  // Sync the input's displayed text whenever quantity changes externally
  // (e.g. via +/− pads, or estimatorQty handoff). Skip the sync if the
  // user is actively typing — we don't want to clobber their keystrokes.
  useEffect(() => {
    if (document.activeElement !== qtyInputRef.current) {
      setQtyDraft(String(Number(quantity) || 12));
    }
  }, [quantity]);
  const [activeTab, setActiveTab] = useState<'overview' | 'specs' | 'docs'>('overview');
  // Custom yardage handed off from the Instant Estimator via ?qty=N (e.g. 17).
  // When set, the page bypasses the standard dropdown and adds-to-cart using
  // the exact estimator yardage via buildDeliveryLineItems, so the 5yd-per-
  // load minimum and the delivery split match the estimator's quote exactly.
  // Clearing it (via the "Change quantity" link) reverts to the dropdown.
  const [estimatorQty, setEstimatorQty] = useState<number | null>(null);
  // Calculator Drawer State
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  // Build flat list of all products for prev/next navigation
  const allProducts = useMemo(() => {
    const list: {
      slug: string;
      name: string;
      categoryName: string;
    }[] = [];
    for (const cat of categories) {
      for (const prod of cat.products) {
        list.push({
          slug: prod.slug,
          name: prod.name,
          categoryName: cat.name
        });
      }
    }
    return list;
  }, []);
  // Find product and its category
  let product = null;
  let category = null;
  for (const cat of categories) {
    const found = cat.products.find(p => p.slug === slug);
    if (found) {
      product = found;
      category = cat;
      break;
    }
  }
  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;
  const handleUseQuantity = (yards: number) => {
    const roundedYards = Math.ceil(yards);
    if (roundedYards === 0) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (roundedYards <= fullLoad) {
      // Single-load: pre-select the dropdown value at the actual yards the
      // customer asked for. Tiny 1–2 yd loads are accepted as-is and priced
      // with the micro-load formula by the cart's billing math.
      const snapped = Math.max(1, roundedYards);
      setQuantity(String(snapped));
      setNumLoads('');
      setIsCalcOpen(false);
    } else if (product && category) {
      // Multi-load: add directly to cart using the shared splitter so the
      // last partial load is billed at the 5yd minimum (e.g. 16yd → 12 + 5).
      // This avoids the old behavior of inflating qty to fullLoad × loads.
      const lineItems = buildDeliveryLineItems(roundedYards, product.name);
      lineItems.forEach(li => {
        addToCart({
          category: category.name,
          product: li.product,
          quantity: li.quantity,
          pricePerYard: product.pricePerYard,
          requestedQuantity: li.requestedQuantity
        });
      });
      // Confirmation persists until the customer chooses Continue
      // Shopping or View Cart — gives them time to decide their next
      // step instead of the old 2s auto-dismiss.
      setAddedFlash(true);
      setIsCalcOpen(false);
    }
  };
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Pre-select quantity from estimator handoff (?qty=N). Runs after product
  // is resolved so we know whether it's light (15yd full load) or heavy (12yd).
  // - Single-load amounts (≤ fullLoad) snap to the dropdown directly.
  // - Multi-load amounts (e.g. 17yd) get stored as estimatorQty and the page
  //   adds-to-cart with the exact yardage on click, preserving the estimator's
  //   5yd-min split (12 + 5) rather than inflating to "2 full loads" (24).
  useEffect(() => {
    const qtyParam = searchParams.get('qty');
    if (!qtyParam) return;
    const n = parseInt(qtyParam, 10);
    // Accept any positive yardage — manual 1–2 yd entries flow through and
    // get priced with the micro-load formula (flat $250 delivery).
    if (isNaN(n) || n < 1) return;
    const pn = product?.name.toLowerCase() || '';
    // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
    const light = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
    const fullLoad = light ? 15 : 12;
    if (n <= fullLoad) {
      const snapped = Math.max(1, Math.min(fullLoad, n));
      setQuantity(String(snapped));
      setNumLoads('');
      setEstimatorQty(null);
    } else {
      // Keep the exact estimator yardage. Dropdown stays at its default;
      // the Add to Truck button will use estimatorQty via buildDeliveryLineItems.
      setEstimatorQty(n);
    }
  }, [slug, searchParams, product?.name]);
  if (!product || !category) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-2870" data-mp="3opcpizc8bwd5">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2871" data-mp="3tofukjs7m2l4">
          Product not found
        </h1>
        <button onClick={() => navigate('/')} className="text-brand-orange hover:underline" data-id="element-2872" data-mp="2jpm47pyeap0">
          Return to Home
        </button>
      </div>;
  }
  const isSpecialty = category.name === 'Specialty Rock';
  const pn = product.name.toLowerCase();
  // 50/50 Mix is HEAVY (12 yd load) — excluded from the light list.
  const isLight = pn.includes('bark') || pn.includes('compost') || pn.includes('topsoil') || pn.includes('play chips');
  const isLimitedTo12 = product.name === 'Builders Sand';
  const handleAddToCart = () => {
    if (quantity === 'multi' && numLoads === '5+') {
      setIsQuoteModalOpen(true);
      return;
    }
    // Estimator handoff takes priority — use the exact custom yardage so the
    // cart line items match the estimator's quote (e.g. 14 → 12 + 5, not 24).
    let finalQuantity: number;
    if (estimatorQty && estimatorQty > 0) {
      finalQuantity = estimatorQty;
    } else if (quantity === 'multi') {
      const deliveries = Number(numLoads);
      const fullLoadYards = isLight ? 15 : 12;
      finalQuantity = fullLoadYards * deliveries;
    } else {
      finalQuantity = Number(quantity);
    }
    // Split into per-delivery line items so the cart shows a clean breakdown
    // (Delivery 1 / Delivery 2 / ...) and the 5yd-per-load minimum is
    // enforced on any partial last load.
    const lineItems = buildDeliveryLineItems(finalQuantity, product.name);
    lineItems.forEach(li => {
      addToCart({
        category: category.name,
        product: li.product,
        quantity: li.quantity,
        pricePerYard: product.pricePerYard,
        requestedQuantity: li.requestedQuantity
      });
    });
    // Confirmation persists until the customer chooses Continue
    // Shopping or View Cart.
    setAddedFlash(true);
  };
  // Multi-load custom-quote redirect REMOVED. The amber 5+ warning
  // inside the readout window is now the SOLE entry point to the
  // QuoteModal — no auto-fire when numLoads flips to '5+'. The user
  // explicitly has to tap the warning to open the contact form,
  // matching the Hero estimator's behavior.
  // Reset the quantity dropdown to the per-product default whenever the
  // user navigates to a different product. React Router reuses the same
  // <Product /> component instance when only the :slug param changes, so
  // the useState lazy initializer above only runs on FIRST mount. Without
  // this effect, picking Medium Bark after viewing 1.25" Minus would
  // leave the dropdown stuck at 12 yd instead of snapping to the 15 yd
  // light-load default.
  useEffect(() => {
    if (product?.name) {
      setQuantity(getDefaultQuantity(product.name));
      setNumLoads('');
    }
    // Intentionally only re-runs on slug change — manual dropdown
    // adjustments by the customer within the same product are preserved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  return <motion.main initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: 'easeOut'
  }} className="py-12 md:py-20 bg-[#f4f7f2] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-2873" data-mp="h6t3unoap2bp">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2874" data-mp="14wdmf5gjdxkg">
        {/* Top nav: Back + Prev/Next */}
        <div className="flex items-center justify-between mb-8" data-id="element-2875" data-mp="zzwxwa5fdh6u">
          <Link to="/#materials" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors" data-id="element-2876" data-mp="v7dkbd6qmm21">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2877" data-mp="q8iv9ti6g19h" />
            Back to Materials
          </Link>
          <div className="flex items-center gap-2" data-id="element-2878" data-mp="1nphisyl52oir">
            {prevProduct ? <Link to={`/shop/${prevProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-[#fffff9] dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={prevProduct.name} data-id="element-2879" data-mp="1iweylh294rf8">
                <ChevronLeft className="w-4 h-4" data-id="element-2880" data-mp="208v8ohujzhy1" />
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2881" data-mp="256wtb77mnpbm">
                  {prevProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2882" data-mp="2a4q0a2gr8ncy">Prev</span>
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2883" data-mp="2e5i7yr9zak74">
                <ChevronLeft className="w-4 h-4" data-id="element-2884" data-mp="2iyl4n8a1y9rj" />
              </span>}
            {nextProduct ? <Link to={`/shop/${nextProduct.slug}`} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-muted dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange bg-[#fffff9] dark:bg-[#333333] border border-stone-200 dark:border-[#444444] rounded-lg transition-colors" title={nextProduct.name} data-id="element-2885" data-mp="2nwmnpt7ha930">
                <span className="hidden sm:inline max-w-[120px] truncate" data-id="element-2886" data-mp="3j7lu5niyk1sq">
                  {nextProduct.name}
                </span>
                <span className="sm:hidden" data-id="element-2887" data-mp="3s5xkdofwwxkn">Next</span>
                <ChevronRight className="w-4 h-4" data-id="element-2888" data-mp="3s5e92qkrp7fl" />
              </Link> : <span className="px-3 py-1.5 text-sm text-stone-300 dark:text-stone-600 cursor-not-allowed" data-id="element-2889" data-mp="3n6unira35xnr">
                <ChevronRight className="w-4 h-4" data-id="element-2890" data-mp="2zijq5y3lmg48" />
              </span>}
          </div>
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(28rem,0.9fr)] md:items-start" data-id="element-2891" data-mp="2vgy6ntwk73l2">
          {/* Independent media surface — visually separate from the cart workspace. */}
          <div className="relative h-72 min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#e6ece3] bg-stone-200 shadow-sm sm:h-96 md:sticky md:top-6 md:h-[42rem] dark:border-[#262626] dark:bg-[#242424]" data-id="element-2892" data-mp="34ftgrh2q4xtn">
            <ProductImageCarousel images={product.images || [product.image]} alt={product.name} data-id="element-2893" data-mp="2brnugpw0v0rp" />
          </div>

          {/* Independent details and ordering surface. */}
          <div className="min-w-0 max-w-full rounded-2xl border border-[#e6ece3] bg-[#fffff9] p-6 shadow-sm sm:p-8 md:p-10 dark:border-[#262626] dark:bg-[#333130]" data-id="element-2894" data-mp="w1eul36441pj">
            <div className="mb-2" data-id="element-2895" data-mp="n81pz3yorbnm">
              <span className="text-xs font-bold text-brand-muted uppercase tracking-wider" data-id="element-2896" data-mp="n936vp2dusd9">
                {category.name}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2897" data-mp="1ez7oxh2o73bb">
              {product.name}
            </h1>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 dark:border-[#444] mb-6" data-id="element-2898" data-mp="15ysxdzz44rzd">
              <button onClick={() => setActiveTab('overview')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'overview' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2899" data-mp="160m6dlx77oaj">
                Overview
                {activeTab === 'overview' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2900" data-mp="9ruuyzib0ghg" />}
              </button>
              <button onClick={() => setActiveTab('specs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'specs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2901" data-mp="3rwy3yvbe5cn5">
                Specs
                {activeTab === 'specs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2902" data-mp="3mzxusbj8vtrt" />}
              </button>
              <button onClick={() => setActiveTab('docs')} className={`pb-3 px-4 text-sm font-bold transition-colors relative ${activeTab === 'docs' ? 'text-brand-orange' : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`} data-id="element-2903" data-mp="3d8k14kdz5o6l">
                Documents
                {activeTab === 'docs' && <motion.div layoutId="prodTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-orange" data-id="element-2904" data-mp="2kltrechkir82" />}
              </button>
            </div>

            <div className="mb-6" data-id="element-2905" data-mp="2fpd41ki70gw1">
              <AnimatePresence mode="wait" data-id="element-2906" data-mp="2tnjgvcrw3g37">
                {activeTab === 'overview' && <motion.div key="overview" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2907" data-mp="2tkoydqsfs5cf">
                    <p className="text-sm text-brand-charcoal/80 dark:text-white/80 mb-6 leading-relaxed" data-id="element-2908" data-mp="3dbah32dszu0l">
                      {product.description}
                    </p>

                    {product.bestFor && product.bestFor.length > 0 && <div className="mb-6" data-id="element-2909" data-mp="3mc8kpjkbxzin">
                        <h3 className="text-xs font-bold text-brand-charcoal dark:text-white uppercase tracking-wider mb-3" data-id="element-2910" data-mp="3hcm5j4kxy977">
                          Best For
                        </h3>
                        <div className="flex flex-wrap gap-2" data-id="element-2911" data-mp="2exdbve4uauvy">
                          {product.bestFor.map((tag, idx) => <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-brand-lightgray dark:bg-[#2a2a2a] text-xs font-medium text-brand-charcoal dark:text-stone-300 border border-stone-200 dark:border-[#444444]" data-id="element-2912" data-mp="2jvx05l6woirh" data-mpi={idx}>
                              {tag}
                            </span>)}
                        </div>
                      </div>}

                    {product.proTip && <div className="border border-[#d4ddcf] dark:border-emerald-500/20 rounded-sm p-4 flex items-start gap-3" data-id="element-2913" data-mp="32qlpx0m36mm2">
                        <div className="bg-[#04A05C]/15 p-1.5 rounded-full shrink-0 mt-0.5" data-id="element-2914" data-mp="3bpyxtoukb34o">
                          <Check className="w-4 h-4 text-[#04A05C] dark:text-[#22c95c]" data-id="element-2915" data-mp="1dachf5bj9do2" />
                        </div>
                        <div data-id="element-2916" data-mp="1hch8pxeidy7j">
                          <h4 className="text-sm font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-2917" data-mp="1hbpp7ky6rqxm">
                            Pro Tip
                          </h4>
                          <p className="text-sm text-brand-charcoal/80 dark:text-stone-300 leading-relaxed" data-id="element-2918" data-mp="1505eckfajydw">
                            {product.proTip}
                          </p>
                        </div>
                      </div>}
                  </motion.div>}

                {activeTab === 'specs' && <motion.div key="specs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2919" data-mp="1sr0ziyp8rvc6">
                    {product.specs ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2920" data-mp="1etcglweqiy9r">
                        {Object.entries(product.specs).map(([key, value]) => {
                    const labels: Record<string, string> = {
                      size: 'Size / Gradation',
                      weightPerYard: 'Density (Weight/Yd)',
                      compaction: 'Compaction Rating',
                      coverage: 'Est. Coverage'
                    };
                    return <div key={key} className="bg-stone-50 dark:bg-[#222222] p-4 rounded-xl border border-stone-200 dark:border-[#333333]" data-id="element-2921" data-mp="1i8vyrkigwl3r">
                              <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-1.5" data-id="element-2922" data-mp="1m73fifcxlmey">
                                {labels[key] || key}
                              </p>
                              <p className="text-sm font-medium text-stone-900 dark:text-stone-200" data-id="element-2923" data-mp="20yvgfmbzaxx5">
                                {value}
                              </p>
                            </div>;
                  })}
                      </div> : <div className="text-center py-8 text-stone-500" data-id="element-2924" data-mp="pidwumthcprx">
                        No detailed specifications available for this product.
                      </div>}
                  </motion.div>}

                {activeTab === 'docs' && <motion.div key="docs" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="space-y-3" data-id="element-2925" data-mp="m33yiwnhixou">
                    {/* Auto-generated Spec Sheet — real, downloadable */}
                    <button onClick={() => generateSpecSheet({
                  product,
                  categoryName: category.name
                })} className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-orange/30 bg-brand-orange/5 hover:bg-brand-orange/10 hover:border-brand-orange/50 transition-all group text-left" data-id="element-2926" data-mp="3uhkxrevjdxjr">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-2927" data-mp="3gnl9sa61b0aq">
                        <div className="w-10 h-10 rounded-lg bg-brand-orange/15 flex items-center justify-center shrink-0" data-id="element-2928" data-mp="3bps55lkdqeck">
                          <FileText className="w-5 h-5 text-brand-orange" data-id="element-2929" data-mp="1flccacrar2el" />
                        </div>
                        <div className="min-w-0" data-id="element-2930" data-mp="1kkf7z6ldzw9w">
                          <p className="text-sm font-bold text-stone-900 dark:text-white" data-id="element-2931" data-mp="1peb03seyyox5">
                            Product Spec Sheet
                          </p>
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate" data-id="element-2932" data-mp="wouat3mnbo55">
                            One-page summary · specs, coverage, pricing
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange shrink-0 ml-3" data-id="element-2933" data-mp="15npi6j1ff4a1">
                        <Download className="w-4 h-4" data-id="element-2934" data-mp="1am978sbbgw6m" />
                        <span className="hidden sm:inline" data-id="element-2935" data-mp="2h3e6peubwbcy">Download</span>
                      </span>
                    </button>

                    {/* Need more? Email us — honest CTA for additional docs */}
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 dark:border-[#444] bg-stone-50 dark:bg-[#2a2a2a]" data-id="element-2936" data-mp="2w64iil1cwx3f">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-[#333] flex items-center justify-center shrink-0" data-id="element-2937" data-mp="2mhe60f8oxqq6">
                        <Mail className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-2938" data-mp="2iezobygrcx0h" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-2939" data-mp="2dhr65xvm0sz8">
                        <p className="text-sm font-bold text-stone-900 dark:text-white mb-0.5" data-id="element-2940" data-mp="28i4nva0xcmjk">
                          Need certified docs?
                        </p>
                        <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed" data-id="element-2941" data-mp="23kutrh2lbmpe">
                          Gradation curves, MSDS, and compaction test data are
                          available on request.{' '}
                          <a href={`mailto:info@206gravel.com?subject=${encodeURIComponent(`Certified docs request: ${product.name}`)}`} className="text-brand-orange font-semibold hover:underline break-all" data-id="element-2942" data-mp="3tuoainm6ts08">
                            info@206gravel.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div data-id="element-2943" data-mp="c5idgqyluz1l">
              {isSpecialty ? <div className="space-y-4" data-id="element-2944" data-mp="3tfgr26ynyi6m">
                  <div className="p-4 bg-brand-lightgray/50 dark:bg-[#222222] rounded-xl border border-stone-200 dark:border-[#444444]" data-id="element-2945" data-mp="3tengeuwl38j7">
                    <p className="text-sm text-brand-charcoal dark:text-stone-300" data-id="element-2946" data-mp="3kfjwxpti8r46">
                      Pricing and availability for specialty rock varies. Please
                      contact us directly for a custom quote.
                    </p>
                  </div>
                  <a href="tel:206-619-3742" className="w-full bg-brand-charcoal dark:bg-white text-white dark:text-brand-charcoal hover:bg-brand-charcoal/90 dark:hover:bg-stone-200 py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center" data-id="element-2947" data-mp="1iluceu994ky8">
                    <Phone className="w-5 h-5 mr-2" data-id="element-2948" data-mp="1st5b5aq3jetc" />
                    Call for Pricing
                  </a>
                </div> : <div className="space-y-4" data-id="element-2949" data-mp="26kjyftr1e267">
                  {(() => {
                const fullLoad = isLight ? 15 : 12;
                const smallLoadThreshold = 10;
                const requestedYards = estimatorQty ? estimatorQty : quantity === 'multi' && numLoads && numLoads !== '5+' ? Number(numLoads) * fullLoad : quantity !== 'multi' ? Number(quantity) : 0;
                const loads: number[] = [];
                if (requestedYards > 0) {
                  if (requestedYards <= fullLoad) {
                    loads.push(requestedYards);
                  } else {
                    const count = Math.ceil(requestedYards / fullLoad);
                    const last = requestedYards - (count - 1) * fullLoad;
                    for (let i = 0; i < count - 1; i++) loads.push(fullLoad);
                    loads.push(last);
                  }
                }
                const billableYards = loads.reduce((a, b) => a + b, 0);
                const bulkPct = billableYards >= fullLoad * 4 ? 0.1 : billableYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                // Build per-delivery line totals so the multi-load
                // breakdown can mirror the Services hub: each
                // delivery shows its yardage and its post-discount
                // dollar amount on its own row. preDiscountTotal is
                // the strikethrough "Was $X" anchor for the bulk
                // discount headline.
                const deliveryLoads: {
                  yards: number;
                  total: number;
                }[] = [];
                let hasSurchargedLoad = false;
                for (const ld of loads) {
                  let sur = 1;
                  if (ld >= 3 && ld < smallLoadThreshold) {
                    sur = 1 + (smallLoadThreshold - ld) * 0.1;
                  }
                  if (sur > 1) hasSurchargedLoad = true;
                  const loadTotal = Math.ceil(product.pricePerYard * sur) * ld;
                  deliveryLoads.push({
                    yards: ld,
                    total: loadTotal
                  });
                }
                const preDiscountTotal = deliveryLoads.reduce((s, l) => s + l.total, 0);
                let totalCost = preDiscountTotal;
                if (bulkPct > 0) {
                  totalCost = Math.round(totalCost * (1 - bulkPct));
                  // Apply the same bulk discount to each per-delivery
                  // total so the rows sum to the discounted grand
                  // total (with at most a rounding-cent of drift).
                  deliveryLoads.forEach((l, i) => {
                    deliveryLoads[i] = {
                      ...l,
                      total: Math.round(l.total * (1 - bulkPct))
                    };
                  });
                }
                const effectiveAvg = billableYards > 0 ? Math.round(totalCost / billableYards) : product.pricePerYard;
                const showsEffective = billableYards > 0 && (hasSurchargedLoad || bulkPct > 0) && effectiveAvg !== product.pricePerYard;
                // Past 4 full loads the order rolls into custom-quote
                // territory. The bulk-discount formula no longer
                // reflects what dispatch will actually charge (hand
                // loads, mixed truck sizes, etc.), so we suppress
                // the savings copy past that threshold rather than
                // surface a stale number.
                const isCustomQuoteZone = deliveryLoads.length >= 5;
                return <div className="border-b border-stone-100 dark:border-[#444444] pb-4" data-id="element-2950" data-mp="ki4hwvopxb02">
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3" data-id="element-2951" data-mp="fkcrk63q1wxz">
                          {/* Left: price column. The label above the
                           big number is state-aware:
                           - Bulk discount tier reached: amber pill
                           "5% Bulk Discount" (uppercase, prominent).
                           - Under the small-load threshold: clickable
                           "Small-load rate ⓘ" — tapping toggles a
                           full-width pulldown explainer below the
                           two-column header.
                           - Otherwise: the default "Price" label. */}
                          <div className="min-w-0 flex-1" data-id="element-2952" data-mp="30wzsalc2ohjl">
                            {/* State label — all three variants render
                             at the same H-6 height so the big "$XX
                             /yard" number stays on the same baseline
                             as the right-column total regardless of
                             which label is showing.
                             Priority order:
                             1. Bulk discount (green pill) — customer
                             paying LESS.
                             2. Small-load surcharge (amber pill) —
                             customer paying MORE. Triggers off
                             hasSurchargedLoad alone so the pill
                             appears even on small-load orders where
                             the rounded effective rate happens to
                             match the base rate.
                             3. Default "Price" label otherwise. */}
                            <div className="h-6 mb-1 flex items-center" data-id="element-2953" data-mp="2ir8wcuta1zv8">
                              {!isCustomQuoteZone && bulkPct > 0 ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#04A05C] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-2954" data-mp="2eq6sg0of0zmx">
                                  {Math.round(bulkPct * 100)}% Bulk Discount
                                </span> : !isCustomQuoteZone && hasSurchargedLoad ? <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#CB6E0A] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-2955" data-mp="3eymz6pkn3hvn">
                                  Small-load rate
                                </span> : <p className="text-sm text-brand-muted dark:text-[#22c95c] font-medium" data-id="element-2956" data-mp="3nx7cfb7fkzch">
                                  Price
                                </p>}
                            </div>
                            {isCustomQuoteZone ? <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-2957" data-mp="6i0i5rhy0d4w">
                                Custom quote
                              </p> : <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white" data-id="element-2958" data-mp="akf62q0gy331">
                                $
                                {showsEffective ? effectiveAvg : product.pricePerYard}{' '}
                                {/* Secondary "/ yard" label uses the
                                 same neutral muted-grey as the right
                                 column's "{N} yds /" and "+ delivery"
                                 so the metadata reads uniformly
                                 regardless of small-load / bulk
                                 discount state. "avg" dropped per
                                 design — the headline rate IS the
                                 customer's per-yard cost. */}
                                <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-2959" data-mp="7d60lgfnv72a">
                                  / yard
                                </span>
                              </p>}
                            {/* Subtext under the big number. For a small
                             load: single line with strikethrough base
                             rate + plain "base rate at N yd+" copy
                             (the clickable label above carries the
                             surcharge messaging now). For a bulk
                             discount: single line with strikethrough
                             base rate + "Save $X on N yards". */}
                            {showsEffective && hasSurchargedLoad && !bulkPct && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5 whitespace-nowrap" data-id="element-2960" data-mp="utq4up74h8ns">
                                  ${product.pricePerYard}/yd at{' '}
                                  {smallLoadThreshold} yd+
                                </p>}
                            {showsEffective && bulkPct > 0 && !isCustomQuoteZone && (
                      /* Savings copy under the price reads
                      accent green (matches the green pill
                      above) — the customer is paying LESS
                      here, so the color reinforces that
                      even when the eye skips the label. */
                      <p className="text-sm mt-1" data-id="element-2961" data-mp="333l3l1elraf">
                                  <span className="line-through text-stone-500 dark:text-stone-400" data-id="element-2962" data-mp="745cxgszr12d">
                                    ${product.pricePerYard}/yd
                                  </span>
                                  <span className="text-[#04A05C] ml-2 font-semibold" data-id="element-2963" data-mp="ilczmhhpbi5d">
                                    $
                                    {product.pricePerYard * billableYards - totalCost}{' '}
                                    savings
                                  </span>
                                </p>)}
                            {!showsEffective && billableYards === 0 && <p className="text-xs text-brand-muted dark:text-stone-400 mt-1.5" data-id="element-2964" data-mp="3vyofbv08zuh8">
                                Base price at {smallLoadThreshold}+ yards per
                                delivery. Smaller loads include a surcharge.
                              </p>}
                          </div>
                          {/* Right: materials subtotal — mirrors the
                           "$87 / yard" pattern on the left by putting the
                           small qualifier ("12 yds /") IN FRONT of the
                           big number ("$1,044"). The leading-blank top
                           row keeps the big numbers baseline-aligned
                           with the Price column even though the right
                           column has no top label. */}
                          {billableYards > 0 && <div className="min-w-0 text-right" data-id="element-2965" data-mp="26h1c1pf9nbgn">
                              {isCustomQuoteZone ? <>
                                  <p className="text-lg font-semibold text-stone-500 dark:text-stone-400 whitespace-nowrap" data-id="element-2966" data-mp="18w5agd4l2muc">
                                    <span className="text-sm font-normal text-brand-muted dark:text-[#22c95c]" data-id="element-2967" data-mp="1dr2k9m9ot08a">
                                      {billableYards} yds /
                                    </span>{' '}
                                    Custom
                                  </p>
                                  <p className="text-xs text-brand-orange mt-0.5" data-id="element-2968" data-mp="q1zyq3bxmn8i">
                                    text us for pricing
                                  </p>
                                </> : <>
                                  {/* Secondary metadata ("{N} yds /"
                                   and "+ delivery") uses the same
                                   neutral muted-grey as the left
                                   column's "/ yard" so the surrounding
                                   metadata reads uniformly white →
                                   grey regardless of discount state.
                                   Only the BIG numbers stay white. */}
                                  <p className="text-3xl font-heading font-bold text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-2969" data-mp="3sjg8sp1pvsn">
                                    <span className="text-base font-normal text-brand-muted dark:text-stone-400" data-id="element-2970" data-mp="8qkyjbendr32">
                                      {billableYards} yds /
                                    </span>{' '}
                                    ${totalCost.toLocaleString()}
                                  </p>
                                  {/* "+ delivery" mirrors the green
                                   "Price" label on the left so the
                                   two columns book-end the row in
                                   matching accent colors. */}
                                  <p className="text-xs text-brand-muted dark:text-[#22c95c] mt-0.5" data-id="element-2971" data-mp="dx9bygtuw3of">
                                    + delivery
                                  </p>
                                </>}
                            </div>}
                        </div>

                        {/* Section-wide explainer pulldown for the
                         Small-load rate label. Spans the entire price
                         card so it has room to breathe (rather than
                         squeezing into the left column under the
                         numbers). */}
                        <AnimatePresence data-id="element-2972" data-mp="3hcx7fycfaqwd">
                          {showSmallLoadInfo && !isCustomQuoteZone && showsEffective && hasSurchargedLoad && !bulkPct && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-2973" data-mp="3iuvmlvgmseo7">
                                <p className="mt-3 p-3 rounded-lg bg-[#CB6E0A]/10 dark:bg-[#F6A93B]/10 border border-[#CB6E0A]/30 dark:border-[#F6A93B]/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed" data-id="element-2974" data-mp="34ub28uv2iasl">
                                  A small load runs the truck below capacity.
                                  Fixed loading costs (loader operator, scale,
                                  ticketing) get split across fewer yards, so
                                  the per-yard rate goes up. Order{' '}
                                  {smallLoadThreshold}+ yd in a single delivery
                                  to drop to the base ${product.pricePerYard}/yd
                                  rate.
                                </p>
                              </motion.div>}
                        </AnimatePresence>

                        {/* Materials breakdown — ALWAYS visible when
                         there's a quantity. Surfaces the per-yard rate
                         in plain language: single small load → 1
                         "Small-load rate" line; single full load or
                         all-full multi-load → 1 "Full-load rate" (or
                         bulk-discount) line; full + partial mix → 2
                         lines so the customer sees exactly which yards
                         run at each rate. */}
                        {billableYards > 0 && !isCustomQuoteZone && (() => {
                    const _fullLoadCount = Math.floor(billableYards / fullLoad);
                    const _fullLoadYards = _fullLoadCount * fullLoad;
                    const _partialYards = billableYards - _fullLoadYards;
                    const _partialSurcharge = _partialYards > 0 ? getSmallLoadSurcharge(_partialYards, product.name) : 1;
                    // Derive the Materials-breakdown numbers
                    // from the source-of-truth `totalCost`
                    // (which has the bulk discount applied at
                    // the whole-order level with proper
                    // rounding). The OLD approach rounded the
                    // per-yard rate first then multiplied,
                    // which drifted from totalCost by several
                    // dollars on bulk-discounted orders
                    // (e.g. 24 yd @ 5% showed $73/yd × 24 =
                    // $1,752 while the actual total was
                    // $1,733). Now the breakdown ALWAYS
                    // reconciles to totalCost exactly.
                    //
                    // Strategy:
                    //   1. Compute the full-load subtotal as
                    //      the pro-rated share of totalCost
                    //      that the full-load yards represent
                    //      (using the un-surcharged base
                    //      rate × discounted price). For pure
                    //      full-load orders this equals
                    //      totalCost.
                    //   2. Compute partial subtotal as
                    //      totalCost − fullLoadSubtotal so
                    //      the two lines always sum to the
                    //      grand total.
                    //   3. Per-yard rates are derived from
                    //      the subtotals so they match what
                    //      the customer is actually paying.
                    const _partialBaseShare = _partialYards * _partialSurcharge;
                    const _fullLoadBaseShare = _fullLoadYards;
                    const _baseShareSum = _fullLoadBaseShare + _partialBaseShare;
                    const fullLoadSubtotal = _fullLoadYards > 0 && _baseShareSum > 0 ? Math.round(totalCost * _fullLoadBaseShare / _baseShareSum) : 0;
                    const partialSubtotal = _partialYards > 0 ? totalCost - fullLoadSubtotal : 0;
                    const fullLoadPerYd = _fullLoadYards > 0 ? Math.round(fullLoadSubtotal / _fullLoadYards) : Math.round(product.pricePerYard * (1 - bulkPct));
                    const partialPerYd = _partialYards > 0 ? Math.round(partialSubtotal / _partialYards) : 0;
                    const hasMixedBreakdown = _fullLoadCount > 0 && _partialYards > 0;
                    const fullLoadTagFull = bulkPct > 0 ? <span className="text-[#04A05C] font-semibold" data-id="element-2975" data-mp="1ns3h81jealwa">
                                  {Math.round(bulkPct * 100)}% bulk discount
                                </span> : null;
                    return <div className="mt-4 pt-3 border-t border-brand-charcoal/10 dark:border-white/10 space-y-1.5" data-id="element-2976" data-mp="cf10n6hapwc4">
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-charcoal/60 dark:text-white/60 mb-1" data-id="element-2977" data-mp="4wta43w1rofc">
                                  Materials
                                </div>
                                {hasMixedBreakdown ? <>
                                    {/* Full-load line */}
                                    <div className="flex items-baseline justify-between gap-3" data-id="element-2978" data-mp="3hblus14acsms">
                                      <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2979" data-mp="3db1wuvbw1kzl">
                                        <span data-id="element-2980" data-mp="3r382volbublb">
                                          ${fullLoadPerYd}/yd × {_fullLoadYards}{' '}
                                          yd
                                        </span>
                                        {fullLoadTagFull}
                                      </div>
                                      <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2981" data-mp="290phc9g755po">
                                        ${fullLoadSubtotal.toLocaleString()}
                                      </div>
                                    </div>
                                    {/* Partial-load line. Only tag it
                                     "Small-load rate" when the partial
                                     actually triggers a surcharge
                                     (below the threshold). A partial
                                     at/above the threshold — e.g. an
                                     11 yd remainder on a 35 yd heavy
                                     order — runs at the full-load rate,
                                     so it gets the full-load (or bulk)
                                     tag instead. */}
                                    <div className="flex items-baseline justify-between gap-3" data-id="element-2982" data-mp="3fowb4yxo58g2">
                                      <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2983" data-mp="141j6eana8qnu">
                                        <span data-id="element-2984" data-mp="z14x6p9y3ao8">
                                          ${partialPerYd}/yd × {_partialYards}{' '}
                                          yd
                                        </span>
                                        {_partialSurcharge > 1 ? <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} aria-expanded={showSmallLoadInfo} className="inline-flex items-center gap-1 text-[#CB6E0A] dark:text-[#F6A93B] font-semibold hover:opacity-90 transition-opacity" data-id="element-2985" data-mp="1ms0tnzpciu1t">
                                            <span className="underline underline-offset-2 decoration-dotted" data-id="element-2986" data-mp="1d04hxluc75f4">
                                              Small-load rate
                                            </span>
                                            <Info className="w-3 h-3" data-id="element-2987" data-mp="20sblb4yhcco1" />
                                          </button> : fullLoadTagFull}
                                      </div>
                                      <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2988" data-mp="32iryivgpcbb9">
                                        ${partialSubtotal.toLocaleString()}
                                      </div>
                                    </div>
                                  </> : _partialYards > 0 && _partialSurcharge > 1 ? (
                      /* Single small-load order BELOW the
                      threshold — surcharge in effect,
                      tag the line "Small-load rate". */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-2989" data-mp="3v5vzmpcoc5hr">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2990" data-mp="3q69k299did5s">
                                      <span data-id="element-2991" data-mp="7ytxechk9ssu">
                                        ${partialPerYd}/yd × {_partialYards} yd
                                      </span>
                                      <button type="button" onClick={() => setShowSmallLoadInfo(v => !v)} aria-expanded={showSmallLoadInfo} className="inline-flex items-center gap-1 text-[#CB6E0A] dark:text-[#F6A93B] font-semibold hover:opacity-90 transition-opacity" data-id="element-2992" data-mp="mowwjet3e12z">
                                        <span className="underline underline-offset-2 decoration-dotted" data-id="element-2993" data-mp="hnzfj5jtjd32">
                                          Small-load rate
                                        </span>
                                        <Info className="w-3 h-3" data-id="element-2994" data-mp="8o3ywl7hupvn" />
                                      </button>
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2995" data-mp="3q2hhcpj61we8">
                                      ${partialSubtotal.toLocaleString()}
                                    </div>
                                  </div>) : _partialYards > 0 ? (
                      /* At-or-above-threshold but below
                      one full load (e.g. 10–11 yd on
                      heavy materials) — surcharge = 1,
                      so it's effectively the full-load
                      rate. Render with the full-load
                      tag (or bulk-discount tag). */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-2996" data-mp="1dvndt3ezvugk">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-2997" data-mp="2al4jvjhw3iis">
                                      <span data-id="element-2998" data-mp="25mta18rlh80k">
                                        ${partialPerYd}/yd × {_partialYards} yd
                                      </span>
                                      {fullLoadTagFull}
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-2999" data-mp="q5t0kkqrk3vs">
                                      ${partialSubtotal.toLocaleString()}
                                    </div>
                                  </div>) : (
                      /* Pure full-load order (1+ full
                      loads, no partial) — one line at
                      the full-load rate or bulk-discount
                      rate. */
                      <div className="flex items-baseline justify-between gap-3" data-id="element-3000" data-mp="3aky8cynugsik">
                                    <div className="min-w-0 text-xs text-brand-charcoal/70 dark:text-white/70 flex items-center flex-wrap gap-x-2" data-id="element-3001" data-mp="3fkjim58gsnal">
                                      <span data-id="element-3002" data-mp="3kht459sqget5">
                                        ${fullLoadPerYd}/yd × {_fullLoadYards}{' '}
                                        yd
                                      </span>
                                      {fullLoadTagFull}
                                    </div>
                                    <div className="text-sm font-bold text-brand-charcoal dark:text-white tabular-nums shrink-0" data-id="element-3003" data-mp="1v6926no7aou8">
                                      ${fullLoadSubtotal.toLocaleString()}
                                    </div>
                                  </div>)}
                                {deliveryLoads.length > 1 && <p className="text-xs text-brand-orange mt-1" data-id="element-3004" data-mp="zwm1zzc4th5u">
                                    + additional delivery fee for{' '}
                                    {deliveryLoads.length - 1} extra{' '}
                                    {deliveryLoads.length > 2 ? 'loads' : 'load'}
                                  </p>}
                                {deliveryLoads.some(l => l.yards > 0 && l.yards < 3) && <p className="text-xs text-amber-600 dark:text-amber-400 mt-1" data-id="element-3005" data-mp="g4fnkusps3i8">
                                    Includes a $250 micro-load delivery fee
                                    (load under 3 yd).
                                  </p>}
                              </div>;
                  })()}
                      </div>;
              })()}

                  {/* Estimator handoff banner — appears when the user
                   arrived from the Instant Estimator with a custom yardage
                   that needs to split across multiple deliveries. Without
                   this chip, the dropdown still shows its default ("12 Yards")
                   and the user thinks the page ignored their calculated
                   amount. Clicking "Change quantity" reverts to the dropdown. */}
                  {estimatorQty && estimatorQty > 0 && (() => {
                const loads = (() => {
                  const fullLoad = isLight ? 15 : 12;
                  const MIN = 5;
                  const n = estimatorQty;
                  if (n <= fullLoad) return [Math.max(MIN, n)];
                  const count = Math.ceil(n / fullLoad);
                  const last = n - (count - 1) * fullLoad;
                  const billableLast = Math.max(MIN, last);
                  const arr: number[] = [];
                  for (let i = 0; i < count - 1; i++) arr.push(fullLoad);
                  arr.push(billableLast);
                  return arr;
                })();
                const breakdown = loads.join(' + ');
                const totalBillable = loads.reduce((a, b) => a + b, 0);
                return <motion.div initial={{
                  opacity: 0,
                  y: -6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="rounded-xl border border-[#04A05C]/30 bg-[#04A05C]/10 p-3 sm:p-4" data-id="element-3006" data-mp="34bg6mlhwvdtb">
                          <div className="flex items-start gap-3" data-id="element-3007" data-mp="2qcq7l0bj742b">
                            <div className="w-8 h-8 rounded-full bg-[#04A05C]/20 flex items-center justify-center shrink-0" data-id="element-3008" data-mp="2qejhcohfy4ej">
                              <Check className="w-4 h-4 text-[#04A05C]" data-id="element-3009" data-mp="2hc1uc7typ7qh" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3010" data-mp="jlqvojkbwc5w">
                              <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-3011" data-mp="jjwi5qbdzmbu">
                                From your estimator: {estimatorQty}{' '}
                                {estimatorQty === 1 ? 'yard' : 'yards'}
                              </p>
                              <p className="text-xs text-brand-charcoal/70 dark:text-stone-300 mt-0.5" data-id="element-3012" data-mp="22fk7a6y68q3i">
                                Splits into {loads.length}{' '}
                                {loads.length === 1 ? 'delivery' : 'deliveries'}{' '}
                                ({breakdown} yd)
                                {totalBillable !== estimatorQty && <span className="text-amber-700 dark:text-amber-400" data-id="element-3013" data-mp="1ji98sokd60p4">
                                    {' '}
                                    · billed as {totalBillable} yd (3 yd order
                                    min)
                                  </span>}
                              </p>
                            </div>
                            <button type="button" onClick={() => setEstimatorQty(null)} className="shrink-0 text-xs font-semibold text-brand-charcoal/70 dark:text-stone-400 hover:text-brand-orange dark:hover:text-brand-orange underline underline-offset-2" data-id="element-3014" data-mp="2yy84h0cso48h">
                              Change
                            </button>
                          </div>
                        </motion.div>;
              })()}

                  <div className="space-y-2" data-id="element-3015" data-mp="2of5g7ei329mb">
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-3016" data-mp="2jmm3skv4tkj3">
                      <label className="block text-sm font-bold text-brand-charcoal dark:text-stone-300" data-id="element-3017" data-mp="2ybs0o26hyynt">
                        {estimatorQty ? 'Or change quantity' : 'Select Quantity'}
                      </label>
                      {(() => {
                    const fullLoad = isLight ? 15 : 12;
                    const tier2Yards = isLight ? fullLoad : fullLoad * 2;
                    const tier3Yards = fullLoad * 4;
                    return <span className="text-[11px] text-brand-muted dark:text-stone-400" data-id="element-3018" data-mp="1czgt8n4txali">
                            5% at {tier2Yards} yd · 10% at {tier3Yards} yd
                          </span>;
                  })()}
                    </div>
                    {/* Floating stepper — no container background, just
                     big bold + and − pads flanking the live readout.
                     active:* gives instant green flash on press. Below
                     a full load increments by 1 yard; at/above a full
                     load snaps to the next full-load multiple (2, 3,
                     4 loads). + at 4 loads flips the row below into an
                     amber "Need 5+ loads?" link (does NOT auto-open the
                     custom-quote modal — the customer explicitly taps
                     the link). */}
                    <div className="flex flex-col gap-3" data-id="element-3019" data-mp="b3kwmgch50ze">
                      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-stretch gap-3 min-h-[68px] sm:min-h-[79px]" data-id="element-3020" data-mp="b44b1n2x178o">
                        <div className="order-2 flex min-w-0 items-center rounded-lg border border-stone-300 bg-transparent px-5 text-2xl font-extrabold leading-none tabular-nums text-brand-charcoal dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white sm:px-6 sm:text-3xl" data-id="element-3021" data-mp="20go88a85q3r4">
                          {(() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        const loadCount = current > fullLoad ? Math.round(current / fullLoad) : 1;
                        const isFullLoad = current === fullLoad;
                        const isMin = current === 3;
                        const commit = () => {
                          const parsed = parseInt(qtyDraft, 10);
                          if (isNaN(parsed) || parsed <= 0) {
                            // Empty / invalid → revert input text to current.
                            setQtyDraft(String(current));
                            return;
                          }
                          // Cap at 8 loads (96 yd heavy / 120 yd light).
                          const max = fullLoad * 8;
                          const clamped = Math.min(max, Math.max(1, parsed));
                          setQuantity(String(clamped));
                          setQtyDraft(String(clamped));
                          // Flip the amber "text us" CTA once past 4 loads.
                          if (clamped > fullLoad * 4) {
                            setShowQuoteWarning(true);
                          } else {
                            setShowQuoteWarning(false);
                          }
                        };
                        return <>
                                <input ref={qtyInputRef} type="text" inputMode="numeric" pattern="[0-9]*" value={qtyDraft} maxLength={2} onFocus={e => e.currentTarget.select()} onChange={e => {
                            // Numeric only, max 2 digits.
                            const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                            setQtyDraft(v);
                          }} onBlur={commit} onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.currentTarget as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setQtyDraft(String(current));
                              (e.currentTarget as HTMLInputElement).blur();
                            }
                          }} aria-label="Quantity in yards" style={{
                            width: `${Math.max(1, qtyDraft.length)}ch`
                          }} className={`bg-transparent border-0 outline-none text-center text-2xl font-bold tabular-nums p-0 m-0 focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3022" data-mp="1utkbs9woe163" />
                                <span className={`hidden sm:inline transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3023" data-mp="hvkdgg4m6ybp">
                                  {' '}
                                  yards
                                </span>
                                <span className={`sm:hidden transition-colors ${isMin ? 'text-[#CB6E0A] dark:text-[#F6A93B]' : 'text-brand-charcoal dark:text-white'}`} data-id="element-3024" data-mp="376b06b41bvo">
                                  {' '}
                                  yd
                                </span>
                                {(loadCount > 1 || loadCount === 1 && Number(quantity) === (isLight ? 15 : 12)) && <span className="ml-2 text-xs sm:text-sm font-medium text-brand-muted dark:text-stone-400 align-baseline whitespace-nowrap" data-id="element-3025" data-mp="3gcy04k8jamfk">
                                    ({loadCount}{' '}
                                    {loadCount === 1 ? 'load' : 'loads'})
                                  </span>}
                              </>;
                      })()}
                        </div>
                        <div className="contents" data-id="element-3026" data-mp="1rl2ezz9fdrk0">
                          <button type="button" onClick={() => {
                        const fullLoad = isLight ? 15 : 12;
                        const MIN = 3;
                        const current = Number(quantity) || 12;
                        if (current <= fullLoad) {
                          if (current > MIN) setQuantity(String(current - 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          const newLoads = currentLoads - 1;
                          setQuantity(String(newLoads * fullLoad));
                          // Clear the amber CTA once back at 4 loads or less.
                          if (newLoads <= 4) setShowQuoteWarning(false);
                        }
                      }} aria-label="Decrease quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="order-1 group flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-transparent text-brand-charcoal transition-colors duration-150 hover:bg-[#059F40]/10 active:bg-[#059F40]/20 active:text-[#059F40] dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white dark:hover:bg-[#333333] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c] sm:h-[72px] sm:w-[72px]" data-id="element-3027" data-mp="1rku4uf4ot0ci">
                            <Minus className="h-8 w-8 transition-transform duration-150 ease-out group-active:scale-125 sm:h-9 sm:w-9" strokeWidth={2.5} data-id="element-3028" data-mp="k9zj1m3qgq46" />
                          </button>
                          <button type="button" onClick={() => {
                        const current = Number(quantity) || 12;
                        const fullLoad = isLight ? 15 : 12;
                        if (current < fullLoad) {
                          setQuantity(String(current + 1));
                        } else {
                          const currentLoads = Math.round(current / fullLoad);
                          // Cap at 8 loads. Past 4 loads the customer
                          // is in "large order" territory and the
                          // primary CTA flips to the amber "text us"
                          // button — but the stepper still works so
                          // they can show us the exact yardage they
                          // need before tapping through to SMS.
                          if (currentLoads >= 8) return;
                          const newLoads = currentLoads + 1;
                          setQuantity(String(newLoads * fullLoad));
                          if (newLoads >= 5) setShowQuoteWarning(true);
                        }
                      }} aria-label="Increase quantity" style={{
                        WebkitTapHighlightColor: 'transparent'
                      }} className="order-3 group flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-transparent text-brand-charcoal transition-colors duration-150 hover:bg-[#059F40]/10 active:bg-[#059F40]/20 active:text-[#059F40] dark:border-white/15 dark:bg-[#2a2a2a] dark:text-white dark:hover:bg-[#333333] dark:active:bg-[#22c95c]/15 dark:active:text-[#22c95c] sm:h-[72px] sm:w-[72px]" data-id="element-3029" data-mp="y37b1evq7nme">
                            <Plus className="h-8 w-8 transition-transform duration-150 ease-out group-active:scale-125 sm:h-9 sm:w-9" strokeWidth={2.5} data-id="element-3030" data-mp="2cxupbl6koqze" />
                          </button>
                        </div>
                      </div>

                      {/* Large-order handoff — when + has pushed past
                       4 loads, the primary CTA below morphs into an
                       amber "Text us for a quote" button instead of a
                       separate amber pulldown. */}

                      {addedFlash ? <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="space-y-3" data-id="element-3031" data-mp="1p4kxs1whrve9">
                          <div className="w-full flex items-center justify-center bg-[#04A05C]/10 dark:bg-[#006241]/40 text-[#04A05C] dark:text-[#22c95c] rounded-none font-bold border border-[#04A05C] dark:border-[#22c95c] py-4 text-lg" data-id="element-3032" data-mp="3p9gfbp6bidff">
                            <Check className="w-5 h-5 mr-2" data-id="element-3033" data-mp="3g8s6r9zp159x" />
                            Added to Truck!
                          </div>
                          <div className="flex gap-2" data-id="element-3034" data-mp="bu0s7jqnyjyj">
                            <button onClick={() => {
                        setAddedFlash(false);
                        navigate('/#materials');
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-brand-charcoal dark:text-white border border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-3035" data-mp="btipq5der9i6">
                              Continue Shopping
                            </button>
                            <button onClick={() => {
                        setAddedFlash(false);
                        openCart();
                      }} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-brand-orange hover:bg-brand-orangeHover transition-colors flex items-center justify-center gap-1.5" data-id="element-3036" data-mp="14vrxl5zz3g4l">
                              <ShoppingCart className="w-4 h-4" data-id="element-3037" data-mp="19obi6mx2vbiw" />
                              View Cart
                            </button>
                          </div>
                        </motion.div> : showQuoteWarning ? <button onClick={() => {
                    const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                    if (!isMobile) {
                      setIsQuoteModalOpen(true);
                      return;
                    }
                    const fullLoad = isLight ? 15 : 12;
                    const yds = Number(quantity) || fullLoad * 5;
                    const loads = Math.max(5, Math.round(yds / fullLoad));
                    const body = `Hi 206Gravel — I need a custom quote for ${loads} loads of ${product.name} (${yds} yd). Thanks!`;
                    window.location.href = `sms:+12066193742?&body=${encodeURIComponent(body)}`;
                  }} className="w-full py-[18px] rounded-none font-bold text-lg transition-all duration-300 flex items-center justify-center bg-[#DD4132]/15 dark:bg-[#DD4132]/15 hover:bg-[#DD4132]/25 dark:hover:bg-[#DD4132]/25 text-[#DD4132] dark:text-[#DD4132] border-2 border-[#DD4132]/40 hover:border-[#DD4132]/60" aria-live="polite" data-id="element-3038" data-mp="hygl242zahjo">
                          <MessageSquare className="w-5 h-5 mr-2" data-id="element-3039" data-mp="53usi9uvd8w8" />
                          Large order — text us →
                        </button> : (() => {
                    const current = Number(quantity) || 12;
                    const fullLoad = isLight ? 15 : 12;
                    const loadPct = Math.min(100, Math.max(0, current / fullLoad * 100));
                    return <button onClick={handleAddToCart} className="relative w-full overflow-hidden bg-[#04702E] text-white py-[21px] rounded-none font-bold text-lg flex items-center justify-center shadow-[0_0_12px_rgba(5,137,77,0.4)]" data-id="element-3040" data-mp="107g98woyhryv">
                              <motion.span aria-hidden="true" className="absolute inset-y-0 left-0 bg-[#0DA24B]" initial={false} animate={{
                        width: `${loadPct}%`
                      }} transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 26
                      }} data-id="element-3041" data-mp="1a6p19k09gwil" />
                              <span className="relative z-10 flex items-center justify-center" data-id="element-3042" data-mp="1swesxy8q926a">
                                <Truck className="w-5 h-5 mr-2" data-id="element-3043" data-mp="1xsvgaq83rcib" />
                                Add to Truck
                              </span>
                            </button>;
                  })()}
                    </div>

                    <div className="flex justify-center" data-id="element-3044" data-mp="26ssaf7d9h3kr">
                      <SameDayBadge variant="full" data-id="element-3045" data-mp="6tnbn127kx1z" />
                    </div>

                    {/* Material sanity check — helps the customer confirm
                     the product they're viewing matches their project
                     BEFORE adding to cart. Lives here (not in the cart
                     drawer) so the decision happens with intent fresh.
                     The helper is now category-aware: each category in
                     data/products.ts ships its own pair of questions
                     and its own recommendation table inside
                     components/CartHelper.tsx, so the suggestions stay
                     relevant whether the customer is browsing base rock,
                     clear & drain rock, soil/mulch, quarry rip-rap, or
                     decorative landscape stone. */}
                    <CartHelper contextProduct={{
                  slug: product.slug,
                  name: product.name
                }} data-id="element-3046" data-mp="31aqkj6c3gqgf" />

                    {/* Multi-load delivery-fee notice — visible whenever
                     the stepper is open. The "please select how many"
                     prompt is no longer needed because the stepper
                     auto-seeds 2 loads on entering multi mode. */}
                    <AnimatePresence data-id="element-3047" data-mp="170k1bx4yd07v">
                      {quantity === 'multi' && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="overflow-hidden" data-id="element-3048" data-mp="16yz3nyncd15s">
                          <div className="flex items-start gap-2 mt-3 p-3 bg-brand-orange/10 border border-brand-orange/20 rounded-lg" data-id="element-3049" data-mp="o1ejh4viuqri">
                            <Info className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-3050" data-mp="2eevhoc7ahs0p" />
                            <p className="text-xs text-brand-charcoal dark:text-stone-300" data-id="element-3051" data-mp="2ienr0r4ic274">
                              Multi-load orders require separate deliveries. You
                              will be charged a delivery fee per load.
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                </div>}
            </div>
          </div>
        </div>

        <section className="mt-10" aria-labelledby="product-support-heading" data-id="element-3052" data-mp="1aqyqhkm3x7c3">
          <div className="mb-5" data-id="element-3053" data-mp="15zqgongv7jye">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#05894e] dark:text-[#4ed491]" data-id="element-3054" data-mp="15yp7kojet7jb">Plan with confidence</p>
            <h2 id="product-support-heading" className="mt-1 font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-3055" data-mp="10yjbmw63wyvf">Resources for your project</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3056" data-mp="r1elymd1yl9v">Get the quantity right, prepare your delivery space, and use practical project guidance before your material arrives.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-3" data-id="element-3057" data-mp="2yqapf7qp8msl">
            <button type="button" onClick={() => setIsCalcOpen(value => !value)} aria-expanded={isCalcOpen} className="rounded-2xl border border-[#05894e]/35 bg-[#05894e]/5 p-5 text-left transition-colors hover:bg-[#05894e]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:bg-[#05894e]/10" data-id="element-3058" data-mp="32p1lrcncc0en">
              <CalcIcon className="h-6 w-6 text-[#05894e] dark:text-[#4ed491]" aria-hidden="true" data-id="element-3059" data-mp="2f1tjl3l7l7gu" />
              <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3060" data-mp="2jzv1hl6wf0qq">Calculate how much you need</h3>
              <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3061" data-mp="2ox3n5vr99mwq">Estimate yards from your project dimensions and apply the result directly to this order.</p>
              <span className="mt-4 inline-flex text-sm font-bold text-[#05894e] dark:text-[#4ed491]" data-id="element-3062" data-mp="1w97rmudfa11j">{isCalcOpen ? 'Hide calculator' : 'Open calculator'}</span>
            </button>
            <Link to="/delivery-prep" className="rounded-2xl border border-stone-200 bg-[#fffff9] p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:bg-[#333130] dark:hover:bg-[#3a3a3a]" data-id="element-3063" data-mp="1t72zhwy7cmj0">
              <Truck className="h-6 w-6 text-[#05894e] dark:text-[#4ed491]" aria-hidden="true" data-id="element-3064" data-mp="1xzw2afa41ksg" />
              <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3065" data-mp="1aabixva1olin">Delivery prep guide</h3>
              <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3066" data-mp="168q9yl0msrdq">Check access, placement, and site conditions so delivery day goes smoothly.</p>
              <span className="mt-4 inline-flex text-sm font-bold text-[#05894e] dark:text-[#4ed491]" data-id="element-3067" data-mp="1f7tuzujczguv">Prepare your site <ArrowRight className="ml-1 h-4 w-4" data-id="element-3068" data-mp="2vm3gmgw7uri0" /></span>
            </Link>
            <Link to="/blog" className="rounded-2xl border border-stone-200 bg-[#fffff9] p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] dark:border-white/10 dark:bg-[#333130] dark:hover:bg-[#3a3a3a]" data-id="element-3069" data-mp="2qnih6josgbum">
              <FileText className="h-6 w-6 text-[#05894e] dark:text-[#4ed491]" aria-hidden="true" data-id="element-3070" data-mp="hchhhneqsabw" />
              <h3 className="mt-4 font-bold text-brand-charcoal dark:text-white" data-id="element-3071" data-mp="3hff3a93t33u">DIY project guides</h3>
              <p className="mt-1 text-sm leading-relaxed text-brand-muted dark:text-stone-400" data-id="element-3072" data-mp="w3qcyd5bspwy">Learn the right material depth, base layers, and installation approach for common projects.</p>
              <span className="mt-4 inline-flex text-sm font-bold text-[#05894e] dark:text-[#4ed491]" data-id="element-3073" data-mp="10768151qyi7j">Explore guides <ArrowRight className="ml-1 h-4 w-4" data-id="element-3074" data-mp="mc442nggn2xd" /></span>
            </Link>
          </div>

          <AnimatePresence data-id="element-3075" data-mp="raxaxx18rvok">
            {isCalcOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.22
          }} className="overflow-hidden" data-id="element-3076" data-mp="1f2cd9pt0qczt">
                <div className="mt-4 rounded-2xl border border-[#05894e]/25 bg-[#fffff9] p-5 dark:bg-[#333130]" data-id="element-3077" data-mp="3b4z1tpyyngsy">
                  <SimpleAreaCalculator materialSlug={product.slug} onApply={handleUseQuantity} data-id="element-3078" data-mp="366xid3xmhfgy" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>

        <ProductComparisonPanel product={product} categoryName={category.name} data-id="element-3079" data-mp="2pfbaypocchl" />
      </div>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} data-id="element-3080" data-mp="3txjp8rvw4tpd" />

      {/* Old fixed-position Calculator Drawer removed — replaced by the
                                                                                                                                                                                                                                                                          inline "Calculate how much I need" pulldown under the Add to Truck
                                                                                                                                                                                                                                                                          button, matching the Services hub product detail layout. */}
    </motion.main>;
}